import Link from 'next/link';
import { Component } from 'react';

import { lazyInject } from '../../../context/module';
import { LoginService } from '../../../services/login.service';
import { SettingsService } from '../../../services/settings.service';
import { AccountDetails } from '../../../utils/request.utils';

import AnonymousPortal from './anonymous-portal.component';
import {NavIcon, NavIconName} from './nav-icons';
import PortalAccountMenu from './portal-account-menu.component';
import PortalIdentityTile from './portal-identity-tile.component';
import styles from './portal-layout.component.module.scss';
import PortalLoading from './portal-loading.component';
import PortalSystemStatus from './portal-system-status.component';
import PortalTopBar, {BreadcrumbItem} from './portal-top-bar.component';

/**
 * Shared portal layout shell — left side-nav + main content slot.
 *
 * Used by every page under `/portal/*` (and `/admin/billing`, which
 * shares the same shell for staff convenience). Sections per
 * payments-redesign.md §8 + developer-api.md §5.2:
 *
 *   Account & Subscription
 *     - My Account               → /portal
 *     - Billing overview         → /portal/billing
 *     - Billing history          → /portal/billing-history
 *     - Usage analytics          → /portal/billing-usage
 *     - Top up credits           → /portal/billing-topup
 *
 *   Developer
 *     - API keys                 → /portal/api-keys
 *     - Webhook endpoints        → /portal/webhooks
 *     - Conversion history       → /portal/conversion-history
 *
 * Design notes:
 *
 * - Class component + `lazyInject` from `../../../context/module`,
 *   matching the rest of the portal (see `portal.component.tsx`,
 *   `api-keys.component.tsx`). No React hooks.
 * - Aesthetic mirrors `portal.component.module.scss` — Stripe-ish
 *   off-white background, rounded white cards, indigo accent.
 * - Highlight: the active nav item is computed against
 *   `window.location.pathname` on mount. On the SSG build the
 *   pre-rendered HTML may briefly show the home item as active
 *   until hydration, which then flips to the actual route.
 * - Developer section (API keys / Webhooks / Conversion history) is
 *   always shown for logged-in portal users — auth is already gated
 *   upstream, and the prior "hide until apiBillingEstablished or
 *   keys.length > 0" flow caused a visible flicker on every load.
 * - Mobile: collapses to a top bar + hamburger drawer at <= 768px,
 *   matching the breakpoint used by `header-v2.component.module.scss`.
 */
interface PortalLayoutProps {
    /** Renderable content for the main slot. */
    children?: any;
    /**
     * Override the auto-detected current path. Useful for tests and
     * cases where the consumer already knows the active route (avoids
     * a window.location read).
     */
    activePath?: string;
    /**
     * Breadcrumb trail rendered in the persistent top bar. Defaults to
     * `[{label: 'Portal'}]` when the caller doesn't supply one. Phase 5
     * will wire real values per page; Phase 2 keeps the generic default
     * so untouched callers still render correctly.
     */
    breadcrumbs?: BreadcrumbItem[];
}

interface PortalLayoutState {
    pathname: string;
    drawerOpen: boolean;
    userDetails: AccountDetails | null;
    /**
     * True once LoginService has resolved the current session — either the
     * boot probe finished (anonymous → emits `null`) or a logged-in details
     * payload arrived. Until then `isUserLoggedIn` is unreliable: the bearer
     * may sit in localStorage with the in-memory flag still false, and a
     * synchronous early-return into <AnonymousPortal/> would bounce a real
     * user off the page before the probe completes.
     */
    sessionResolved: boolean;
    /**
     * Backend FeatureFlags.portalPlansV2Enabled. Defaults false so the
     * "Plans" nav item stays hidden until settings load. Subscribed in
     * componentDidMount via SettingsService.featureFlags (ReplaySubject,
     * fires immediately if settings already loaded).
     */
    portalPlansV2Enabled: boolean;
    /**
     * Backend FeatureFlags.portalV3Enabled. When true, the sidebar renders
     * the new 4-section IA (Overview/Conversions/Usage + Developer + Billing
     * + Settings). When false (the default until settings load), the legacy
     * 2-section IA is shown. Phase 5 will collapse this to v3-only.
     */
    portalV3Enabled: boolean;
}

interface NavItem {
    label: string;
    href: string;
    /** Optional: explicit match if a route also matches more specific child. */
    matchPaths?: string[];
    icon: NavIconName;
}

interface NavSection {
    label: string;
    items: NavItem[];
}

export default class PortalLayout extends Component<PortalLayoutProps, PortalLayoutState> {
    @lazyInject(LoginService) private loginService!: LoginService;
    @lazyInject(SettingsService) private settingsService!: SettingsService;

    state: PortalLayoutState = {
        pathname: this.props.activePath || (typeof window !== 'undefined' ? window.location.pathname : '/portal'),
        drawerOpen: false,
        userDetails: this.loginService?.userDetails ?? null,
        // On a soft-nav between portal pages LoginService already has the
        // session; treat it as resolved synchronously so the boot loader
        // doesn't flash. Cold loads start false and flip on first emission.
        sessionResolved: !!this.loginService?.isUserLoggedIn || this.loginService?.userDetails != null,
        portalPlansV2Enabled: false,
        portalV3Enabled: false,
    };

    componentDidMount() {
        // Refresh current path on mount in case the consumer didn't pass it
        // and we were rendered SSG-style with a stale window snapshot.
        if (!this.props.activePath && typeof window !== 'undefined') {
            this.setState({ pathname: window.location.pathname });
        }

        this.loginService.onUserDetailsChanged.subscribe({
            next: (details) => this.setState({ userDetails: details, sessionResolved: true }),
        });

        // featureFlags is a ReplaySubject(1) — emits the current value to
        // late subscribers as soon as /v1/api/settings (or the SSR-provided
        // settings prop) has been processed by SettingsService.
        this.settingsService?.featureFlags?.subscribe({
            next: (ff) => this.setState({
                portalPlansV2Enabled: ff?.portalPlansV2Enabled === true,
                portalV3Enabled: ff?.portalV3Enabled === true,
            }),
        });
    }

    /**
     * Nav sections computed at render time so the conditional "Plans"
     * entry can react to the portalPlansV2Enabled feature flag without a
     * forceUpdate dance.
     */
    private sections(): NavSection[] {
        const flagOn = this.state.portalPlansV2Enabled;
        return [
            {
                label: 'Account & Subscription',
                items: [
                    { label: 'My Account', href: '/portal', matchPaths: ['/portal', '/portal/'], icon: 'overview' },
                    { label: 'Billing overview', href: '/portal/billing', icon: 'plan' },
                    ...(flagOn ? [{ label: 'Plans', href: '/portal/plans', icon: 'plan' as NavIconName }] : []),
                    { label: 'Invoices', href: '/portal/invoices', icon: 'invoices' },
                    { label: 'Payment history', href: '/portal/billing-history', icon: 'history' },
                    { label: 'Usage analytics', href: '/portal/billing-usage', icon: 'usage' },
                    { label: 'Top up credits', href: '/portal/billing-topup', icon: 'credits' },
                ],
            },
            {
                label: 'Developer',
                items: [
                    { label: 'API keys', href: '/portal/api-keys', icon: 'apiKeys' },
                    { label: 'Webhook endpoints', href: '/portal/webhooks', icon: 'webhooks' },
                    { label: 'Conversion history', href: '/portal/conversion-history', icon: 'conversions' },
                ],
            },
        ];
    }

    /**
     * Phase 3 v3 IA — flat 4-section layout. Selected at render time when
     * `state.portalV3Enabled` is true. Kept separate from `sections()` so
     * the legacy IA remains untouched while the flag is rolled out; Phase 5
     * will retire the legacy method and inline these as the only sections.
     */
    private sectionsV3(): NavSection[] {
        return [
            {
                label: '',
                items: [
                    { label: 'Overview', href: '/portal', matchPaths: ['/portal', '/portal/'], icon: 'overview' },
                    { label: 'Conversions', href: '/portal/conversions', icon: 'conversions' },
                    { label: 'Usage', href: '/portal/usage', icon: 'usage' },
                ],
            },
            {
                label: 'Developer',
                items: [
                    { label: 'API keys', href: '/portal/api-keys', icon: 'apiKeys' },
                    { label: 'Webhooks', href: '/portal/webhooks', icon: 'webhooks' },
                ],
            },
            {
                label: 'Billing',
                items: [
                    { label: 'Plan', href: '/portal/plan', icon: 'plan' },
                    { label: 'Credits & top-ups', href: '/portal/credits', icon: 'credits' },
                    { label: 'Invoices', href: '/portal/invoices', icon: 'invoices' },
                    { label: 'Payment history', href: '/portal/billing-history', icon: 'history' },
                ],
            },
            {
                label: 'Settings',
                items: [
                    { label: 'Account', href: '/portal/settings', icon: 'settings' },
                ],
            },
        ];
    }

    private toggleDrawer = () => {
        this.setState((s) => ({ drawerOpen: !s.drawerOpen }));
    };

    private closeDrawer = () => {
        this.setState({ drawerOpen: false });
    };

    private isActive(item: NavItem): boolean {
        const path = this.state.pathname.replace(/\/$/, '') || '/';
        const candidates = item.matchPaths || [item.href];
        return candidates.some((p) => {
            const normalized = p.replace(/\/$/, '') || '/';
            return path === normalized;
        });
    }

    private renderNavList(forMobile = false) {
        const ud = this.state.userDetails;
        const planLabel = ud?.packageType === 'PACKAGE_TYPE_PRO' ? 'Pro'
            : ud?.packageType === 'PACKAGE_TYPE_BASIC' ? 'Basic'
            : undefined;
        const sectionList = this.state.portalV3Enabled ? this.sectionsV3() : this.sections();
        return (
            <div className={styles.navInner}>
                <PortalIdentityTile
                    email={ud?.email ?? ''}
                    planLabel={planLabel}
                />
                {sectionList.map((section, idx) => {
                    return (
                        <div key={section.label || `section-${idx}`}>
                            {section.label && (
                                <div className={styles.sectionLabel}>{section.label}</div>
                            )}
                            <ul className={styles.navList}>
                                {section.items.map((item) => {
                                    const active = this.isActive(item);
                                    const cls = active
                                        ? `${styles.navLink} ${styles.navLinkActive}`
                                        : styles.navLink;
                                    return (
                                        <li key={item.href} className={styles.navItem}>
                                            <Link
                                                className={cls}
                                                href={item.href}
                                                aria-current={active ? 'page' : undefined}
                                                onClick={forMobile ? this.closeDrawer : undefined}
                                            >
                                                <NavIcon name={item.icon} />
                                                <span>{item.label}</span>
                                            </Link>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    );
                })}
                <div className={styles.navFooter}>
                    <PortalSystemStatus />
                </div>
            </div>
        );
    }

    render() {
        // Wait for the LoginService boot probe to finish before deciding
        // whether to render the anonymous shell. Otherwise a logged-in user
        // who hard-loads a portal sub-page (bearer in localStorage but
        // `isUserLoggedIn` still false in memory) gets bounced into
        // <AnonymousPortal/> for a beat before the probe restores their
        // session — and on some pages the bounce sticks.
        if (!this.state.sessionResolved) {
            return <PortalLoading message="Loading your account…" />;
        }
        if (!this.loginService?.isUserLoggedIn) {
            return <AnonymousPortal />;
        }
        const drawerClass = this.state.drawerOpen ? `${styles.shell} ${styles.open}` : styles.shell;
        return (
            <div className={drawerClass}>
                {/* Mobile top bar — only visible at <= 768px */}
                <div className={styles.mobileBar}>
                    <button
                        type="button"
                        className={styles.mobileToggle}
                        aria-label={this.state.drawerOpen ? 'Close menu' : 'Open menu'}
                        aria-expanded={this.state.drawerOpen}
                        onClick={this.toggleDrawer}
                    >
                        {this.state.drawerOpen ? (
                            <svg
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                aria-hidden="true"
                            >
                                <path
                                    d="M5 5L15 15M15 5L5 15"
                                    stroke="currentColor"
                                    strokeWidth="1.6"
                                    strokeLinecap="round"
                                />
                            </svg>
                        ) : (
                            <svg
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                aria-hidden="true"
                            >
                                <path
                                    d="M3 6H17M3 10H17M3 14H17"
                                    stroke="currentColor"
                                    strokeWidth="1.6"
                                    strokeLinecap="round"
                                />
                            </svg>
                        )}
                    </button>
                    <span className={styles.mobileBarTitle}>Portal</span>
                    <div className={styles.mobileBarActions}>
                        <PortalAccountMenu />
                    </div>
                </div>

                {/* Mobile drawer + backdrop */}
                <div
                    className={styles.mobileBackdrop}
                    aria-hidden="true"
                    onClick={this.closeDrawer}
                />
                <nav className={styles.mobileDrawer} aria-label="Portal navigation (mobile)">
                    {this.renderNavList(true)}
                </nav>

                {/* Desktop side-nav — sticky, always visible at > 768px */}
                <nav className={styles.sideNav} aria-label="Portal navigation">
                    {this.renderNavList(false)}
                </nav>

                {/* Main content slot with desktop top bar */}
                <div className={styles.mainColumn}>
                    <PortalTopBar
                        breadcrumbs={this.props.breadcrumbs ?? [{label: 'Portal'}]}
                        rightSlot={<PortalAccountMenu />}
                    />
                    <main className={styles.main}>{this.props.children}</main>
                </div>
            </div>
        );
    }
}
